'use client';

import { useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { ArrowBackIosNew, ArrowForwardIos } from '@mui/icons-material';
import { Stack, Typography, IconButton, Paper } from '@mui/material';
import { Box } from '@mui/system';
import { EventItem } from '@scorenco/components';
import { RouteLink, Types } from '@scorenco/core';

import { RouteKey } from '@/routes';

import { EventsByRoundIdDocument } from '../Competition.query.generated';

type CompetitionRoundProps = {
  competition?: Types.Competitions_Competition_Detail;
  roundId?: number;
};

export const CompetitionRound = ({
  competition,
  roundId,
}: CompetitionRoundProps) => {
  const nbRounds = competition?.rounds?.length || 0;

  // Calcul de l'index du round sélectionné basé sur roundId
  const initialRoundIndex = useMemo(() => {
    const index =
      competition?.rounds?.findIndex(({ id }) => id === roundId) ?? -1;
    return index < 0 ? 0 : index;
  }, [competition?.rounds, roundId]);

  const [selectedRound, setSelectedRound] = useState(initialRoundIndex);
  const [prevInitialRoundIndex, setPrevInitialRoundIndex] =
    useState(initialRoundIndex);

  if (prevInitialRoundIndex !== initialRoundIndex) {
    setPrevInitialRoundIndex(initialRoundIndex);
    setSelectedRound(initialRoundIndex);
  }

  const eventsDetails = useQuery(EventsByRoundIdDocument, {
    variables: {
      roundId: competition?.rounds?.[selectedRound]?.id as number,
    },
    skip: !competition?.rounds?.[selectedRound]?.id,
    context: {
      role: 'anonymous',
    },
  });

  if (!nbRounds) {
    return null;
  }

  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Matchs
      </Typography>

      <Paper variant="bordered">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: '100%', mb: 2 }}
        >
          <IconButton
            color="primary"
            onClick={() =>
              selectedRound !== 0
                ? setSelectedRound(selectedRound - 1)
                : setSelectedRound(nbRounds - 1)
            }
          >
            <ArrowBackIosNew />
          </IconButton>
          <Typography variant="h4" sx={{ textTransform: 'uppercase' }}>
            {competition?.rounds?.[selectedRound]?.name}
          </Typography>
          <IconButton
            color="primary"
            onClick={() =>
              selectedRound !== nbRounds - 1
                ? setSelectedRound(selectedRound + 1)
                : setSelectedRound(0)
            }
          >
            <ArrowForwardIos />
          </IconButton>
        </Stack>
        <Stack direction="column" spacing={2}>
          {competition?.rounds?.[selectedRound]?.events?.map((event) => (
            <RouteLink
              key={event.id}
              href={{
                routeKey: RouteKey.Event,
                params: {
                  sport: competition?.sport?.slug,
                  event: `${
                    event?.event_with_same_team?.slug
                  }-${event?.event_with_same_team?.id?.toString(36)}`,
                  e: event?.id,
                },
              }}
            >
              <EventItem
                sx={{
                  textDecoration: 'none',
                  color: 'inherit',
                }}
                event={eventsDetails.data?.competitions_event_detail_by_round_day_id?.find(
                  (e) => e.id === event.id
                )}
                showCompetition={false}
                showDate={true}
              />
            </RouteLink>
          ))}
        </Stack>
      </Paper>
    </Box>
  );
};
